import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { getCurrentPosition, getCityFromCoords } from '../utils/helpers'
import PolicyDocumentModal from '../components/common/PolicyDocumentModal'
import { POLICY_DOCUMENTS } from '../constants/policyDocuments'
import { 
  TrophyIcon, 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon, 
  PhoneIcon, 
  MapPinIcon,
  CalendarIcon 
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const Register = () => {
  const { t } = useTranslation()
  const { register } = useAuth()
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [detectingLocation, setDetectingLocation] = useState(false)
  const [needsPreviousPassword, setNeedsPreviousPassword] = useState(false)
  const [previousPassword, setPreviousPassword] = useState('')
  const [policyConsent, setPolicyConsent] = useState(false)
  const [openPolicy, setOpenPolicy] = useState(null)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    city: '',
    birthDate: '',
    age: '',
    sports: 'calcio',
    role: ''
  })

  const calculateAgeFromBirthDate = (birthDateValue) => {
    if (!birthDateValue) return ''

    const birthDate = new Date(birthDateValue)
    if (Number.isNaN(birthDate.getTime())) return ''

    const today = new Date()
    let years = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      years -= 1
    }

    return years >= 0 ? String(years) : ''
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === 'birthDate') {
      const calculatedAge = calculateAgeFromBirthDate(value)
      setFormData({ ...formData, birthDate: value, age: calculatedAge })
      return
    }

    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const calculatedAge = calculateAgeFromBirthDate(formData.birthDate)
    if (!calculatedAge) {
      toast.error(t('register.invalidBirthDate', { defaultValue: 'Please select a valid birth date' }))
      return
    }

    if (Number(calculatedAge) < 1) {
      toast.error(t('register.invalidAge', { defaultValue: 'Age must be at least 1 year' }))
      return
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('errors.passwordMatch'))
      return
    }
    
    if (formData.password.length < 6) {
      toast.error(t('errors.password'))
      return
    }

    if (needsPreviousPassword && !previousPassword) {
      toast.error(t('register.previousPasswordRequired'))
      return
    }

    if (!policyConsent) {
      toast.error(t('register.policyConsentRequired'))
      return
    }
    
    setLoading(true)
    const payload = {
      ...formData,
      age: calculatedAge,
      previousPassword: needsPreviousPassword ? previousPassword : undefined
    }
    const result = await register(formData.email, formData.password, payload)

    if (result.needsPreviousPassword) {
      setNeedsPreviousPassword(true)
      toast.error(t('register.previousPasswordRequired'))
      setLoading(false)
      return
    }

    if (result.success) {
      navigate('/')
    }
    setLoading(false)
  }

  const handleDetectLocation = async () => {
    setDetectingLocation(true)
    try {
      const position = await getCurrentPosition()
      const city = await getCityFromCoords(position.latitude, position.longitude)
      if (city) {
        setFormData({ ...formData, city })
        toast.success(t('location.cityDetected'))
      } else {
        toast.error(t('location.cityNotFound'))
      }
    } catch (error) {
      console.error('Error detecting location:', error)
      toast.error(t('location.errorGettingLocation'))
    } finally {
      setDetectingLocation(false)
    }
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 py-12 ${darkMode ? 'bg-gradient-to-br from-sport-primary/20 via-sport-dark to-sport-darker' : 'bg-gradient-to-br from-gray-50 via-white to-cyan-50'}`}>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`rounded-2xl shadow-2xl max-w-2xl w-full p-8 ${darkMode ? 'bg-sport-dark/90 border border-sport-silver/20 text-sport-light' : 'bg-white border border-gray-200 text-gray-900'}`}
      >
        <motion.div className="text-center mb-8">
          <TrophyIcon className="h-16 w-16 text-sport-primary mx-auto" />
          <h1 className="text-3xl font-bold mt-4 bg-gradient-to-r from-sport-primary to-sport-secondary bg-clip-text text-transparent">
            {t('register.createAccount')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('register.joinAndStartPlaying')}
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.firstName')} *
              </label>
              <motion.div className="relative">
                <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
            </motion.div>

            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.lastName')} *
              </label>
              <motion.div className="relative">
                <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('auth.email')} *
            </label>
            <motion.div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-modern pl-10"
                required
              />
            </motion.div>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.password')} *
              </label>
              <motion.div className="relative">
                <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
            </motion.div>

            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.confirmPassword')} *
              </label>
              <motion.div className="relative">
                <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
            </motion.div>
          </motion.div>

          {needsPreviousPassword && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3"
            >
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('register.previousPasswordHint')}
              </p>
              <motion.div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('register.previousPassword')} *
                </label>
                <motion.div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    name="previousPassword"
                    value={previousPassword}
                    onChange={(e) => setPreviousPassword(e.target.value)}
                    className="input-modern pl-10"
                    required
                    autoComplete="current-password"
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          )}

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.phone')} *
              </label>
              <motion.div className="relative">
                <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
            </motion.div>

            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.city')} *
              </label>
              <motion.div className="relative flex gap-2">
                <motion.div className="relative flex-1">
                  <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input-modern pl-10"
                    required
                  />
                </motion.div>
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={detectingLocation}
                  className="px-3 py-2 bg-sport-primary/20 text-sport-primary rounded-lg hover:bg-sport-primary/30 transition disabled:opacity-50"
                  title={t('location.detectLocation')}
                >
                  {detectingLocation ? (
                    <motion.div className="w-5 h-5 border-2 border-sport-primary border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <MapPinIcon className="h-5 w-5" />
                  )}
                </button>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('register.birthDate', { defaultValue: 'Birth date' })} *
              </label>
              <motion.div className="relative">
                <CalendarIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  className="input-modern pl-10"
                  required
                />
              </motion.div>
              {formData.age && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {t('auth.age')}: {formData.age} {t('common.years', { defaultValue: 'years' })}
                </p>
              )}
            </motion.div>

            <motion.div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('sports.sport')} *
              </label>
              <select
                name="sports"
                value={formData.sports}
                onChange={handleChange}
                className="input-modern"
                required
              >
                <option value="calcio">⚽ {t('sports.calcio')}</option>
                <option value="padel">🎾 {t('sports.padel')}</option>
                <option value="both">🎯 {t('sports.both')}</option>
              </select>
            </motion.div>
          </motion.div>

          <motion.div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('register.yourRole')} *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="input-modern"
              required
            >
              <option value="">{t('register.selectYourRole')}</option>
              {formData.sports === 'calcio' && (
                <>
                  <option value="goalkeeper">🧤 {t('role.goalkeeper')}</option>
                  <option value="defender">🛡️ {t('role.defender')}</option>
                  <option value="midfielder">⚡ {t('role.midfielder')}</option>
                  <option value="attacker">🎯 {t('role.attacker')}</option>
                </>
              )}
              {formData.sports === 'padel' && (
                <>
                  <option value="left">⬅️ {t('role.left')}</option>
                  <option value="right">➡️ {t('role.right')}</option>
                </>
              )}
              {formData.sports === 'both' && (
                <>
                  <optgroup label={t('sports.calcio')}>
                    <option value="goalkeeper">🧤 {t('role.goalkeeper')}</option>
                    <option value="defender">🛡️ {t('role.defender')}</option>
                    <option value="midfielder">⚡ {t('role.midfielder')}</option>
                    <option value="attacker">🎯 {t('role.attacker')}</option>
                  </optgroup>
                  <optgroup label={t('sports.padel')}>
                    <option value="left">⬅️ {t('role.left')}</option>
                    <option value="right">➡️ {t('role.right')}</option>
                  </optgroup>
                </>
              )}
            </select>
          </motion.div>

          <motion.div
            className={`rounded-xl border p-4 ${darkMode ? 'border-sport-silver/20 bg-sport-gray/10' : 'border-gray-200 bg-gray-50'}`}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={policyConsent}
                onChange={(e) => setPolicyConsent(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-sport-primary focus:ring-sport-primary"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('register.policyConsentPrefix')}{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    setOpenPolicy('privacy')
                  }}
                  className="text-sport-primary hover:text-sport-secondary font-semibold underline underline-offset-2"
                >
                  {t('register.privacyPolicy')}
                </button>
                {' '}
                {t('register.policyConsentJoiner')}{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    setOpenPolicy('cookie')
                  }}
                  className="text-sport-primary hover:text-sport-secondary font-semibold underline underline-offset-2"
                >
                  {t('register.cookiePolicy')}
                </button>
                .
              </span>
            </label>
          </motion.div>

          <button
            type="submit"
            disabled={loading || !policyConsent}
            className="btn-gradient w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <motion.div className="flex items-center justify-center">
                <motion.div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></motion.div>
                {t('common.loading')}
              </motion.div>
            ) : (
              needsPreviousPassword
                ? t('register.completeReregistration', { defaultValue: 'Complete registration' })
                : t('register.createAccountButton')
            )}
          </button>
        </form>

        <motion.div className="mt-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('register.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-sport-primary hover:text-sport-secondary font-semibold">
              {t('register.signIn')}
            </Link>
          </p>
        </motion.div>
      </motion.div>

      <PolicyDocumentModal
        open={openPolicy === 'privacy'}
        onClose={() => setOpenPolicy(null)}
        title={t('register.privacyPolicyTitle')}
        documentUrl={POLICY_DOCUMENTS.privacy}
      />
      <PolicyDocumentModal
        open={openPolicy === 'cookie'}
        onClose={() => setOpenPolicy(null)}
        title={t('register.cookiePolicyTitle')}
        documentUrl={POLICY_DOCUMENTS.cookie}
      />
    </div>
  )
}

export default Register
